// Starting point for the proposal's rich-text document ("Gerar a partir dos
// itens"): the same facts buildProposalText / budgetPdf print, as markdown the
// operator then edits in Tiptap. Kept pure so it's unit-testable.
import { itemQty, itemUnit, totalHours, type SummarizableItem } from "./budgetSummary";
import { estimateBusinessDays } from "./proposalText";
import { barterSplit, recurringBilling } from "./barter";
import { formatBRL } from "@/lib/format";

export interface ProposalMarkdownBudget {
  title: string;
  description?: string | null;
  gross_total: number;
  net_total: number;
  recurring_monthly_total?: number;
  platform: string;
  platform_fee?: number;
  discount_percent?: number;
  barter_percent?: number;
  barter_total?: number;
  barter_description?: string | null;
  barter_charge_recurring?: boolean | null;
  items: SummarizableItem[];
}

export const PROPOSAL_INCLUDED = [
  "Desenvolvimento completo do escopo acordado",
  "Revisões e ajustes durante a execução",
  "Deploy e configuração em ambiente de produção",
  "Suporte pós-entrega por 7 dias",
  "Comunicação direta via WhatsApp ou e-mail",
];

const PLATFORM_LABELS: Record<string, string> = {
  direct: "Cliente Direto",
  workana: "Workana",
  other: "Outra Plataforma",
};

const fmt = (v: number) => formatBRL(v);

export function buildProposalMarkdown(
  b: ProposalMarkdownBudget,
  clientName?: string | null,
): string {
  const items = b.items ?? [];
  const oneTime = items.filter((i) => i.pricing_mode !== "recurring");
  const itemsGross = oneTime.reduce((s, i) => s + i.subtotal, 0);
  const discount = Math.max(itemsGross - b.gross_total, 0);
  const fee = b.gross_total - b.net_total;
  const recurring =
    b.recurring_monthly_total ??
    items.filter((i) => i.pricing_mode === "recurring").reduce((s, i) => s + i.subtotal, 0);
  const days = estimateBusinessDays(totalHours(items));

  const rows = items.map(
    (i) =>
      `| ${i.title} | ${itemQty(i)} | ${itemUnit(i)} | ${fmt(i.subtotal)}${i.pricing_mode === "recurring" ? "/mês" : ""} |`,
  );
  const totals: string[] = [];
  if (oneTime.length > 1) totals.push(`| **Subtotal dos serviços** | | | ${fmt(itemsGross)} |`);
  if (discount > 0)
    totals.push(`| Desconto do pacote (${b.discount_percent ?? ""}%) | | | – ${fmt(discount)} |`);
  if (fee > 0)
    totals.push(`| Taxa ${PLATFORM_LABELS[b.platform] ?? b.platform} | | | – ${fmt(fee)} |`);
  totals.push(`| **Valor total** | | | **${fmt(b.net_total)}** |`);
  const barter = barterSplit(b.net_total, b.barter_percent ?? 0);
  if (barter.barterTotal > 0) {
    const pct = Number.isInteger(barter.barterPercent)
      ? String(barter.barterPercent)
      : barter.barterPercent.toFixed(1);
    totals.push(`| Permuta (${pct}%) | | | – ${fmt(barter.barterTotal)} |`);
    totals.push(`| **Valor a cobrar** | | | **${fmt(barter.cashTotal)}** |`);
  }

  const sections: (string | null)[] = [
    `# Proposta comercial — ${b.title}`,
    clientName ? `Preparada para **${clientName}**.` : null,
    "",
    "## Escopo",
    b.description?.trim() || "_Descreva aqui o escopo do projeto._",
    "",
    "## Investimento",
    "| Serviço | Qtd. | Unitário | Subtotal |",
    "| --- | --- | --- | --- |",
    ...rows,
    ...totals,
    "",
    barter.barterTotal > 0
      ? [
          "## Contrapartida (permuta)",
          "",
          barter.isFull
            ? "Este acordo é uma **permuta integral**: nenhum valor será cobrado em dinheiro."
            : `**${fmt(barter.barterTotal)}** do valor será liquidado em serviços; **${fmt(barter.cashTotal)}** será cobrado em dinheiro.`,
          b.barter_description?.trim() ? `\n**Em troca:** ${b.barter_description.trim()}` : null,
          "",
        ]
          .filter((l): l is string => l !== null)
          .join("\n")
      : null,
    recurring > 0
      ? recurringBilling({
          recurring_monthly_total: recurring,
          barter_percent: b.barter_percent,
          barter_charge_recurring: b.barter_charge_recurring,
        }).inBarter
        ? `## Mensalidade\n\n**${fmt(recurring)}/mês — incluída na permuta.** A manutenção mensal faz parte da contrapartida acordada; não há cobrança recorrente.\n`
        : `## Mensalidade\n\n**${fmt(recurring)}/mês** — cobrança recorrente para manter o sistema no ar, faturada automaticamente todo mês.\n`
      : null,
    "## O que está incluso",
    ...PROPOSAL_INCLUDED.map((l) => `- ${l}`),
    "",
    "## Prazo estimado",
    days ? `${days} dias úteis (considerando 6h produtivas/dia).` : "A definir.",
    "",
    "## Validade",
    "Esta proposta é válida por 15 dias a partir da data de emissão.",
  ];

  return sections.filter((l): l is string => l !== null).join("\n");
}
